import React, { memo, useEffect, useState } from 'react';
import { CmsExpander } from '../../CmsExpander/CmsExpander';
import { CmsTabs } from '../../CmsTabs/CmsTabs';
import type { DLClickEventHandlerProps, CarouselEntryType, ColumnEntryType } from 'tsconfig/types';
import {
  CmsAccordion,
  CmsBanner,
  CmsContainer,
  CmsImage,
  CmsLink,
  CmsMenuBanner,
  CmsRichText,
  CmsTimer,
  CmsVideoComponent,
  CmsArticle,
  CmsCarousel,
  CmsConditionalContainer,
  CmsNavigation,
  CmsProductCard,
  CmsText,
  CmsWidget,
  logger,
  CmsMultiCaseSelector,
  CmsHtmlCode,
  CmsVoucherWidget,
} from 'common-ui';
import CmsColumns from '../../CmsColumns/CmsColumns';
import get from 'lodash/get';
import { MemoizedRenderDynamicComponents } from '../CmsContentSection';
import { CarouselSkeleton } from '../../../Skeleton/Carousel/Carousel';
import { CmsContentTypeId } from 'tsconfig/types/Contentful.types';
import { createHomepageDataLayerService } from '../../../../data-layer/homepageDataLayerService';

type RenderDynamicComponentProps = {
  contentItem: any;
  dlClickEventHandler?: DLClickEventHandlerProps;
  renderInPageLevel?: boolean | ((contentType?: CmsContentTypeId) => boolean);
  layoutIndex?: number;
};

export const RenderDynamicComponent = memo((props: RenderDynamicComponentProps): any => {
  const { contentItem, dlClickEventHandler, renderInPageLevel, layoutIndex = 0 } = props;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Set to true only after component mounts on the client
    setIsClient(true);
  }, []);

  if (!contentItem) {
    return null;
  }

  const contentTypeId = get(contentItem, 'sys.contentType.sys.id');
  const instanceId = get(contentItem, 'sys.id');
  const entryTitle = get(contentItem, 'fields.entryTitle', get(contentItem, 'fields.title', 'unknown'));

  // Initialize homepage data layer service
  const dataLayerService = createHomepageDataLayerService({
    renderInPageLevel,
    fallbackClickHandler: dlClickEventHandler,
  });

  // Create click event handler for standard components
  const contentClickEvents = (typeId: CmsContentTypeId) =>
    dataLayerService.createStandardClickEventHandler(contentItem, layoutIndex, typeId);

  switch (contentTypeId) {
    case CmsContentTypeId.Text:
      return <CmsText contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.ProductCard:
      return <CmsProductCard contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.Widget:
      return <CmsWidget contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.Link:
      return (
        <CmsLink
          contentItem={contentItem}
          key={instanceId}
          category="main navbar link"
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );

    case CmsContentTypeId.Image:
      return (
        <CmsImage
          contentItem={contentItem}
          key={instanceId}
          isNextImg
          width={400}
          height={400}
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );

    case CmsContentTypeId.Video:
      return <CmsVideoComponent contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.RichText:
      return (
        <CmsRichText
          contentItem={contentItem}
          key={instanceId}
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );

    case CmsContentTypeId.Banner: {
      const BannerComponent =
        contentItem.fields.variant === 'Multipurpose banner with Calls to Action'
          ? CmsBanner.BannerWithContentAndCTA
          : CmsBanner;
      return (
        <BannerComponent
          contentItem={contentItem}
          key={instanceId}
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );
    }
    case CmsContentTypeId.MenuBanner:
      return (
        <CmsMenuBanner
          contentItem={contentItem}
          key={instanceId}
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );

    case CmsContentTypeId.Accordion:
      return <CmsAccordion contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.Container: {
      const dlEvent = dataLayerService.createContainerEventHandler(contentItem, entryTitle);
      return (
        <CmsContainer
          key={instanceId}
          backgroundColour={contentItem.fields?.backgroundColour}
          alignment={contentItem.fields?.alignment}
          pageWidthToggle={contentItem.fields?.pageWidthToggle}
          fluidWidth={contentItem.fields?.fluidWidth}
        >
          <MemoizedRenderDynamicComponents
            componentDataList={contentItem.fields?.content}
            dlClickEventHandler={dlEvent}
            renderInPageLevel={typeId => Boolean(typeId === CmsContentTypeId.Columns && renderInPageLevel)}
          />
        </CmsContainer>
      );
    }
    case CmsContentTypeId.Navigation:
      return (
        <CmsNavigation
          contentItem={contentItem}
          key={instanceId}
          className="navigation-link"
          dlClickEventHandler={dlClickEventHandler}
        >
          <MemoizedRenderDynamicComponents
            componentDataList={contentItem.fields?.content}
            dlClickEventHandler={(d: string) => dlClickEventHandler?.(`${entryTitle}:${d}`)}
          />
        </CmsNavigation>
      );
    case CmsContentTypeId.Article:
      return (
        <CmsArticle
          contentItem={contentItem}
          key={instanceId}
          dlClickEventHandler={contentClickEvents(contentTypeId)}
        />
      );
    case CmsContentTypeId.Carousel: {
      const slidesContent = contentItem.fields?.slides || [];
      if (!isClient) {
        return <CarouselSkeleton slidesPerBlock={contentItem.fields.slidesPerBlock} slideContent={slidesContent} />;
      }
      const slides = (slidesContent as CarouselEntryType['fields']['slides'])?.map((content, index) => (
        <CmsCarousel.CmsCarouselSlide
          key={`carousel-${content?.sys?.id}-${index + 1}`}
          dataTestId={`slide-${content?.sys?.id}`}
        >
          <RenderDynamicComponent
            contentItem={content}
            dlClickEventHandler={(data: string) =>
              dataLayerService.createCarouselClickEventHandler(contentItem, layoutIndex)(data, index + 1)
            }
          />
        </CmsCarousel.CmsCarouselSlide>
      ));
      return (
        <CmsCarousel
          slidesPerBlock={contentItem.fields?.slidesPerBlock}
          slidesToScroll={contentItem.fields?.slidesToScroll}
          key={instanceId}
          dataTestId={`carousel-${entryTitle}`}
        >
          {slides}
        </CmsCarousel>
      );
    }
    case CmsContentTypeId.Columns: {
      const columnsContent = contentItem.fields?.content || [];
      const columns = columnsContent.map((columnContent: ColumnEntryType, index: number) => (
        <CmsColumns.CmsColumn contentItem={columnContent} key={`${columnContent?.sys?.id} column-${index + 2}`}>
          <MemoizedRenderDynamicComponents
            componentDataList={columnContent?.fields?.content}
            dlClickEventHandler={dataLayerService.createColumnClickEventHandler(contentItem, columnContent)}
          />
        </CmsColumns.CmsColumn>
      ));
      return (
        <CmsColumns contentItem={columnsContent} key={instanceId}>
          {columns}
        </CmsColumns>
      );
    }
    case CmsContentTypeId.ConditionalContainer:
      return (
        <CmsConditionalContainer contentItem={contentItem}>
          <MemoizedRenderDynamicComponents
            componentDataList={contentItem.fields?.content}
            dlClickEventHandler={dlClickEventHandler}
            renderInPageLevel={renderInPageLevel}
          />
        </CmsConditionalContainer>
      );
    case CmsContentTypeId.Expander:
      return (
        <CmsExpander
          entryTitle={contentItem?.fields?.entryTitle}
          expanderTitle={contentItem?.fields?.expanderTitle}
          closerTitle={contentItem?.fields?.closerTitle}
          backgroundColor={contentItem?.fields?.backgroundColor}
          titleColor={contentItem?.fields?.titleColor}
        >
          <RenderDynamicComponent contentItem={contentItem?.fields?.content} />
        </CmsExpander>
      );

    case CmsContentTypeId.Tabs: {
      const tabPanes = contentItem.fields?.tabPanes || [];
      return <CmsTabs tabPanes={tabPanes} key={instanceId} />;
    }

    case CmsContentTypeId.Timer:
      return <CmsTimer contentItem={contentItem} />;

    case CmsContentTypeId.MultiCaseSelector:
      return <CmsMultiCaseSelector contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.HtmlCode:
      return <CmsHtmlCode contentItem={contentItem} key={instanceId} />;

    case CmsContentTypeId.VoucherWidget:
      return <CmsVoucherWidget contentItem={contentItem} key={instanceId} />;

    default:
      logger.error('matching component not found for content item ', contentTypeId);
      return undefined;
  }
});
